import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import scheduleAPI from "../../../../services/schedule";
import slotsAPI from "../../../../services/slots";
import classroomsAPI from "../../../../services/classrooms";
import classesAPI from "../../../../services/classes";
import levelsAPI from "../../../../services/levels";
import trainingsAPI from "../../../../services/trainings";
import MeaButton from "../../../../components/MeaButton";
import UpdateScheduleModal from "./UpdateScheduleModal";
import { Tooltip } from "primereact/tooltip";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";

const ScheduleAssignmentsComponent = forwardRef((props, ref) => {
  const {
    showToolbar = true,
    schoolId,
    trainingId,
  } = props;

  const accessToken = "";
  const navigate = useNavigate();
  const updateModalRef = useRef();
  const [schedule, setSchedule] = useState(null);
  const [isResettingCapacities, setIsResettingCapacities] = useState(false);
  const [selectedLevelId, setSelectedLevelId] = useState("");

  const trainingQuery = trainingsAPI.GetOne(schoolId, trainingId, accessToken);
  const trainingName = trainingQuery?.data?.data?.name || "";

  const queryList = scheduleAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000
  );

  const slotsQuery = slotsAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000
  );

  const classroomsQuery = classroomsAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000
  );

  const levelsQuery = levelsAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000
  );

  useImperativeHandle(ref, () => ({
    refresh: () => {
      queryList?.refetch();
      slotsQuery?.refetch();
      classroomsQuery?.refetch();
      levelsQuery?.refetch();
    },
    getSelectedElements: () => [],
  }));

  const filteredScheduleData = useMemo(() => {
    const scheduleData = queryList?.data?.data || [];
    if (!selectedLevelId) {
      return scheduleData;
    }

    return scheduleData.filter((item) => {
      const itemLevelId = item?.classe?.level?.id || item?.level?.id || item?.level_id;
      return itemLevelId === selectedLevelId;
    });
  }, [queryList?.data?.data, selectedLevelId]);

  const visibleSlotIds = useMemo(() => {
    return new Set(
      filteredScheduleData
        .map((item) => item?.slot_id || item?.slot?.id)
        .filter(Boolean)
    );
  }, [filteredScheduleData]);

  const visibleClassroomIds = useMemo(() => {
    return new Set(
      filteredScheduleData
        .map((item) => item?.classroom_id || item?.classroom?.id)
        .filter(Boolean)
    );
  }, [filteredScheduleData]);

  // Sort slots by show_order if defined, otherwise by weekday (Monday first) and start_hour
  const sortedSlots = useMemo(() => {
    const slots = [...(slotsQuery?.data?.data || [])].filter(
      (slot) => !selectedLevelId || visibleSlotIds.has(slot.id)
    );
    return slots.sort((a, b) => {
      if (a.show_order !== b.show_order && (a.show_order !== undefined || b.show_order !== undefined)) {
        return (a.show_order || 0) - (b.show_order || 0);
      }
      const dayA = a.weekday === 0 ? 7 : a.weekday;
      const dayB = b.weekday === 0 ? 7 : b.weekday;
      if (dayA !== dayB) {
        return dayA - dayB;
      }
      return (a.start_hour || "").localeCompare(b.start_hour || "");
    });
  }, [slotsQuery?.data?.data, selectedLevelId, visibleSlotIds]);

  // Sort classrooms by show_order if defined, otherwise by name
  const sortedClassrooms = useMemo(() => {
    const classrooms = [...(classroomsQuery?.data?.data || [])].filter(
      (classroom) => !selectedLevelId || visibleClassroomIds.has(classroom.id)
    );
    return classrooms.sort((a, b) => {
      if (a.show_order !== b.show_order && (a.show_order !== undefined || b.show_order !== undefined)) {
        return (a.show_order || 0) - (b.show_order || 0);
      }
      return (a.name || "").localeCompare(b.name || "", undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });
  }, [classroomsQuery?.data?.data, selectedLevelId, visibleClassroomIds]);

  // Index schedule items by slot_id and classroom_id
  const scheduleMatrix = useMemo(() => {
    const matrix = {};

    filteredScheduleData.forEach((item) => {
      const slotId = item.slot_id || item.slot?.id;
      const classroomId = item.classroom_id || item.classroom?.id;
      if (slotId && classroomId) {
        const key = `${slotId}_${classroomId}`;
        if (!matrix[key]) {
          matrix[key] = [];
        }
        matrix[key].push(item);
      }
    });

    return matrix;
  }, [filteredScheduleData]);

  // Format hour for display, e.g. "14:00" -> "14h", "09:00" -> "9h", "14:30" -> "14h30"
  const formatHour = (t) => {
    if (!t) return "";
    if (t.includes("h")) return t;
    const parts = t.split(":");
    if (parts.length >= 2) {
      const h = parseInt(parts[0], 10);
      const m = parts[1];
      if (m === "00") return `${h}h`;
      return `${h}h${m}`;
    }
    return t;
  };

  const formatTimeRange = (start, end) => {
    if (!start && !end) return "";
    const s = formatHour(start);
    const e = formatHour(end);
    if (s && e) return `${s}-${e}`;
    return s || e;
  };

  // Badge background color for classes: uses level color or white by default
  const getBadgeStyle = (levelColor) => {
    if (levelColor && levelColor.trim() !== "") {
      return {
        backgroundColor: levelColor,
        color: "#000000",
      };
    }
    return {
      backgroundColor: "#ffffff",
      color: "#000000",
    };
  };

  const getTeacherName = (teacher) => {
    if (!teacher || !teacher.member) return "";
    if (teacher.member.name) return teacher.member.name;
    if (teacher.member.lastname && teacher.member.firstname) {
      return `${teacher.member.lastname} ${teacher.member.firstname}`;
    }
    return teacher.member.lastname || teacher.member.firstname || "";
  };

  const normalizeCapacity = (capacity) => {
    if (capacity === undefined || capacity === null || capacity === "") {
      return null;
    }
    const parsedCapacity = Number(capacity);
    return Number.isFinite(parsedCapacity) && parsedCapacity > 0
      ? parsedCapacity
      : null;
  };

  const classroomCapacityMap = useMemo(() => {
    return sortedClassrooms.reduce((acc, classroom) => {
      const normalized = normalizeCapacity(classroom?.capacity);
      if (normalized !== null) {
        acc[classroom.id] = normalized;
      }
      return acc;
    }, {});
  }, [sortedClassrooms]);

  const getCapacityAlertInfo = (item, classroom) => {
    const classCapacity = normalizeCapacity(item?.classe?.max_capacity);
    const classroomCapacity = normalizeCapacity(
      classroom?.capacity ?? item?.classroom?.capacity
    );

    if (classCapacity === null || classroomCapacity === null) {
      return null;
    }

    if (classCapacity === classroomCapacity) {
      return null;
    }

    return {
      classCapacity,
      tooltip: `Capacité de la classe : ${classCapacity} - capacité de la salle : ${classroomCapacity}`,
    };
  };

  const classesToUpdate = useMemo(() => {
    const classCapacitiesToApply = {};

    filteredScheduleData.forEach((item) => {
      const classId = item?.classe?.id || item?.class_id;
      const classroomId = item?.classroom_id || item?.classroom?.id;
      const assignedCapacity = normalizeCapacity(
        item?.classroom?.capacity ?? classroomCapacityMap[classroomId]
      );

      if (!classId || assignedCapacity === null) {
        return;
      }

      const currentAssignedCapacity = classCapacitiesToApply[classId];
      if (!currentAssignedCapacity || assignedCapacity > currentAssignedCapacity) {
        classCapacitiesToApply[classId] = assignedCapacity;
      }
    });

    return Object.entries(classCapacitiesToApply).filter(([classId, capacity]) => {
      const classEntry = filteredScheduleData.find(
        (item) => (item?.classe?.id || item?.class_id) === classId
      );
      return normalizeCapacity(classEntry?.classe?.max_capacity) !== capacity;
    });
  }, [classroomCapacityMap, filteredScheduleData]);

  const levelOptions = useMemo(() => {
    const levels = levelsQuery?.data?.data || [];
    return [
      { id: "", name: "Tous les niveaux" },
      ...levels,
    ];
  }, [levelsQuery?.data?.data]);

  const executeResetClassCapacities = async () => {
    if (classesToUpdate.length === 0) {
      return;
    }

    setIsResettingCapacities(true);
    try {
      await Promise.all(
        classesToUpdate.map(async ([classId, capacity]) => {
          await classesAPI.UpdateOne(schoolId, trainingId, accessToken, classId, {
            max_capacity: capacity,
          });
        })
      );
      queryList?.refetch?.();
    } catch (error) {
      console.error("Error resetting class capacities:", error);
      confirmDialog({
        header: "Erreur",
        message: "Une erreur s'est produite lors de la remise des capacités.",
        icon: "pi pi-times-circle",
        acceptLabel: "OK",
        rejectVisible: false,
        accept: () => {},
      });
    } finally {
      setIsResettingCapacities(false);
    }
  };

  const resetClassCapacities = () => {
    if (classesToUpdate.length === 0) {
      return;
    }

    const confirmationMessage = `Synchroniser la capacité de ${classesToUpdate.length} classe(s) avec celle des salles affectées ?`;
    confirmDialog({
      message: confirmationMessage,
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Oui",
      rejectLabel: "Non",
      defaultFocus: "reject",
      accept: () => {
        executeResetClassCapacities();
      },
      reject: () => {},
    });
  };

  const startToolbarTemplate = (
    <div className="no-print">
      <div className="flex flex-row align-items-center flex-wrap gap-2">
        <Dropdown
          value={selectedLevelId}
          options={levelOptions}
          optionLabel="name"
          optionValue="id"
          onChange={(e) => setSelectedLevelId(e.value || "")}
          placeholder="Filtrer par niveau"
          className="mr-2"
          style={{ minWidth: "220px" }}
          showClear
        />
        <MeaButton
          label="Imprimer"
          icon="pi pi-print"
          size="small"
          className="p-button mr-2 action"
          onClick={() => {
            navigate(
              `/school/${schoolId}/admin/trainings/${trainingId}/schedule/print-assignments`
            );
          }}
          data-pr-tooltip="Imprimer le tableau"
        />
        <MeaButton
          label={
            isResettingCapacities
              ? "Synchronisation..."
              : "Synchroniser les capacités"
          }
          icon="pi pi-refresh"
          size="small"
          outlined
          className="p-button action"
          disabled={isResettingCapacities || classesToUpdate.length === 0}
          onClick={resetClassCapacities}
          tooltip={
            classesToUpdate.length === 0
              ? "Toutes les capacités des classes sont déjà synchronisées avec les salles affectées"
              : "Synchroniser les capacités des classes avec celles des salles affectées"
          }
          tooltipOptions={{ position: "top" }}
        />
      </div>
    </div>
  );

  // Loading state
  if (queryList.isLoading || slotsQuery.isLoading || classroomsQuery.isLoading || levelsQuery.isLoading) {
    return (
      <div
        className="flex justify-content-center align-items-center"
        style={{ height: "200px" }}
      >
        <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
      </div>
    );
  }

  // Error state
  if (queryList.error || slotsQuery.error || classroomsQuery.error || levelsQuery.error) {
    return (
      <div
        className="flex justify-content-center align-items-center"
        style={{ height: "200px" }}
      >
        <div className="text-red-500">Erreur lors du chargement des données</div>
      </div>
    );
  }

  return (
    <>
      <Tooltip target=".assignment-capacity-tooltip" position="top" />
      <ConfirmDialog />
      <style>{`
        .assignments-print-header {
          display: none;
        }

        .assignments-container {
          overflow-x: auto;
          background: white;
          border-radius: 6px;
        }

        .assignments-table {
          width: 100%;
          border-collapse: collapse;
          border: 2px solid #333333;
          background: #ffffff;
          font-family: inherit;
        }

        .assignments-table th,
        .assignments-table td {
          border: 1.5px solid #444444;
          text-align: center;
          vertical-align: middle;
        }

        .assignments-corner-header {
          background: #ffffff;
          padding: 12px 16px;
          font-weight: 700;
          font-size: 1.1rem;
          color: #000000;
          min-width: 160px;
          width: 180px;
        }

        .assignments-classroom-header {
          background: #ffffff;
          padding: 10px 12px;
          min-width: 130px;
          color: #000000;
        }

        .assignments-classroom-name {
          font-weight: 700;
          font-size: 1.05rem;
          margin-bottom: 2px;
          color: #000000;
        }

        .assignments-classroom-capacity {
          font-weight: normal;
          font-size: 0.95rem;
          color: #6b7280;
        }

        .assignments-slot-header {
          background: #ffffff;
          padding: 12px 14px;
          min-width: 160px;
          width: 180px;
          color: #000000;
        }

        .assignments-slot-name {
          font-weight: 700;
          font-size: 1.05rem;
          line-height: 1.3;
          margin-bottom: 6px;
          color: #000000;
        }

        .assignments-slot-hours {
          font-weight: 700;
          font-size: 1rem;
          color: #000000;
        }

        .assignments-cell {
          padding: 8px 6px;
          height: 100px;
          min-height: 100px;
          background: #ffffff;
        }

        .assignment-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          padding: 4px 6px;
          border-radius: 4px;
          cursor: pointer;
          transition: background-color 0.15s ease, transform 0.15s ease;
        }

        .assignment-item:hover {
          background-color: #f1f5f9;
          transform: translateY(-1px);
        }

        .assignment-class-badge {
          display: inline-block;
          font-weight: 700;
          font-size: 0.95rem;
          padding: 2px 6px;
          border-radius: 3px;
          text-align: center;
          max-width: 100%;
          word-break: break-word;
          line-height: 1.2;
        }

        .assignment-course {
          font-weight: normal;
          font-size: 0.85rem;
          color: #374151;
          line-height: 1.2;
          text-align: center;
        }

        .assignment-class-capacity {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          font-size: 0.8rem;
          font-weight: 600;
          color: #374151;
          line-height: 1.2;
          text-align: center;
        }

        .assignment-class-capacity-warning {
          color: #f59e0b;
          font-size: 0.8rem;
        }

        .assignment-teacher {
          font-weight: 700;
          font-size: 1rem;
          color: #000000;
          line-height: 1.2;
          text-align: center;
        }

        .assignment-count {
          font-weight: 700;
          font-size: 1.05rem;
          color: #000000;
          line-height: 1.2;
          text-align: center;
        }

        @media print {
          @page {
            size: A4 landscape;
            margin: 0.8cm;
          }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
            background-color: #ffffff !important;
          }

          .no-print,
          .p-breadcrumb,
          header,
          nav,
          .p-toast,
          .p-dialog-mask {
            display: none !important;
          }

          .assignments-print-header {
            display: block !important;
            text-align: center;
            margin-bottom: 14px;
          }

          .assignments-print-title {
            font-size: 1.4rem;
            font-weight: 700;
            margin: 0 0 6px 0;
            color: #000000;
          }

          .assignments-print-subtitle {
            font-size: 1.1rem;
            font-weight: 600;
            margin: 0;
            color: #333333;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .assignments-container {
            overflow: visible !important;
            box-shadow: none !important;
          }

          .assignments-table {
            width: 100% !important;
            border: 2px solid #000000 !important;
            page-break-inside: auto;
          }

          .assignments-table th,
          .assignments-table td {
            border: 1.5px solid #000000 !important;
          }

          .assignments-table tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }

          .assignment-item {
            transform: none !important;
            box-shadow: none !important;
          }
        }
      `}</style>

      {/* Toolbar */}
      {showToolbar && (
        <div className="no-print flex justify-content-between align-items-center mb-3">
          <div>{startToolbarTemplate}</div>
        </div>
      )}

      {/* Print Header */}
      <div className="assignments-print-header">
        <h1 className="assignments-print-title">
          Tableau récapitulatif des répartitions des classes
        </h1>
        {trainingName && (
          <h2 className="assignments-print-subtitle">{trainingName}</h2>
        )}
      </div>

      {/* Affectations Matrix Table */}
      <div className="assignments-container">
        {selectedLevelId && filteredScheduleData.length === 0 && (
          <div className="p-3 text-center text-600">
            Aucune affectation ne correspond au niveau sélectionné.
          </div>
        )}
        <table className="assignments-table">
          <thead>
            <tr>
              <th className="assignments-corner-header">Salle</th>
              {sortedClassrooms.map((classroom) => (
                <th key={classroom.id} className="assignments-classroom-header">
                  <div className="assignments-classroom-name">
                    {classroom.name}
                  </div>
                  {classroom.capacity > 0 && (
                    <div className="assignments-classroom-capacity">
                      Effectif max {classroom.capacity}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedSlots.map((slot) => (
              <tr key={slot.id}>
                <th className="assignments-slot-header">
                  <div className="assignments-slot-name">{slot.name}</div>
                  <div className="assignments-slot-hours">
                    {formatTimeRange(slot.start_hour, slot.end_hour)}
                  </div>
                </th>
                {sortedClassrooms.map((classroom) => {
                  const key = `${slot.id}_${classroom.id}`;
                  const items = scheduleMatrix[key] || [];

                  return (
                    <td key={`${slot.id}-${classroom.id}`} className="assignments-cell">
                      {items.map((item) => {
                        const teacherName = getTeacherName(item.teacher);
                        const className = item.classe?.name || "";
                        const courseName = item.course?.name || "";
                        const levelColor =
                          item.classe?.level?.color || item.level?.color;
                        const capacityAlertInfo = getCapacityAlertInfo(
                          item,
                          classroom
                        );

                        return (
                          <div
                            key={item.id}
                            className="assignment-item"
                            onClick={() => {
                              setSchedule(item);
                              if (updateModalRef.current) {
                                updateModalRef.current.show(() => {
                                  queryList.refetch();
                                });
                              }
                            }}
                          >
                            {className && (
                              <div
                                className="assignment-class-badge"
                                style={getBadgeStyle(levelColor)}
                              >
                                {className}
                              </div>
                            )}
                            {capacityAlertInfo && (
                              <div className="assignment-class-capacity">
                                <span>({capacityAlertInfo.classCapacity})</span>
                                <i
                                  className="pi pi-exclamation-triangle assignment-class-capacity-warning assignment-capacity-tooltip"
                                  data-pr-tooltip={capacityAlertInfo.tooltip}
                                ></i>
                              </div>
                            )}
                            {courseName && (
                              <div className="assignment-course">
                                {courseName}
                              </div>
                            )}
                            {teacherName && (
                              <div className="assignment-teacher">
                                {teacherName}
                              </div>
                            )}
                            {item.classe_total_students !== undefined &&
                              item.classe_total_students !== null && (
                                <div className="assignment-count">
                                  {item.classe_total_students}
                                </div>
                              )}
                          </div>
                        );
                      })}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <UpdateScheduleModal
        ref={updateModalRef}
        schedule={schedule}
        schoolId={schoolId}
        trainingId={trainingId}
        accessToken={accessToken}
      />
    </>
  );
});

export default ScheduleAssignmentsComponent;
